/**
 * H5 互动活动埋点 SDK（platform=h5）
 * - 本地队列批量上报 `/analytics/collect`（匿名接口，自动带 token → 解析 uid）
 * - 会话 sessionId 存 sessionStorage，用于还原「浏览链路」
 * - 上下文：activityCode / storeCode / openid(h5Uuid)，所有事件自动带上
 */
import request from '@/utils/request'
import { APP_API_BASE } from '@/utils/api-base'
import { SIGNSTORE_H5_UID_KEY } from '@/utils/signstore-h5-uid'

const QUEUE_KEY = 'h5_party_queue_v1'
const SESSION_KEY = 'h5_party_session'
const CTX_KEY = 'h5_party_ctx'
const MAX_QUEUE = 200
const FLUSH_SIZE = 20
const FLUSH_MS = 10000
const MAX_BATCH = 50
const MAX_STAY_MS = 30 * 60 * 1000
const HEARTBEAT_MS = 30000
const PLATFORM = 'h5'

let flushTimer = null
let flushing = false
let heartbeatTimer = null
let heartbeatStartAt = 0
let visibilityBound = false
let ctx = null

function nowMs() {
  return Date.now()
}

function uuid() {
  return 'e' + nowMs().toString(36) + Math.random().toString(36).slice(2, 10)
}

function readJson(key) {
  try {
    const raw = window.localStorage.getItem(key)
    return raw ? JSON.parse(raw) : null
  } catch (e) {
    return null
  }
}

function writeJson(key, val) {
  try {
    window.localStorage.setItem(key, JSON.stringify(val))
  } catch (e) {
    /* ignore */
  }
}

function readSession() {
  try {
    const s = window.sessionStorage.getItem(SESSION_KEY)
    return s != null ? String(s).trim() : ''
  } catch (e) {
    return ''
  }
}

function writeSession(id) {
  try {
    window.sessionStorage.setItem(SESSION_KEY, id)
  } catch (e) {
    /* ignore */
  }
}

function ensureSession() {
  const old = readSession()
  if (old) return old
  const id = 's' + uuid()
  writeSession(id)
  return id
}

function loadQueue() {
  const q = readJson(QUEUE_KEY)
  return Array.isArray(q) ? q : []
}

function saveQueue(list) {
  writeJson(QUEUE_KEY, (list || []).slice(-MAX_QUEUE))
}

function currentPage() {
  try {
    const hash = window.location.hash || ''
    const i = hash.indexOf('?')
    const path = i >= 0 ? hash.slice(1, i) : hash.slice(1)
    return path ? '/' + path.replace(/^\/+/, '') : ''
  } catch (e) {
    return ''
  }
}

function currentContext() {
  if (ctx) return ctx
  const stored = readJson(CTX_KEY)
  ctx = stored && typeof stored === 'object' ? stored : {}
  return ctx
}

/**
 * 设置埋点上下文（进入互动活动时调用一次）
 * @param {{activityCode?: string, storeCode?: string, openid?: string, source?: string}} options
 */
export function setPartyTrackContext(options) {
  const o = options || {}
  const base = currentContext()
  const next = Object.assign({}, base)
  if (o.activityCode != null) next.activityCode = String(o.activityCode).trim()
  if (o.storeCode != null) next.storeCode = String(o.storeCode).trim()
  if (o.openid != null) next.openid = String(o.openid).trim()
  if (o.source != null) next.source = String(o.source).trim()
  if (next.openid == null || next.openid === '') {
    try {
      const h5 = window.localStorage.getItem(SIGNSTORE_H5_UID_KEY)
      if (h5) next.openid = String(h5).trim()
    } catch (e) {
      /* ignore */
    }
  }
  ctx = next
  writeJson(CTX_KEY, next)
  return next
}

export function getPartyTrackContext() {
  return Object.assign({}, currentContext())
}

export function clearPartyTrackContext() {
  ctx = {}
  try {
    window.localStorage.removeItem(CTX_KEY)
  } catch (e) {
    /* ignore */
  }
}

function scheduleFlush() {
  if (flushTimer) return
  flushTimer = setTimeout(() => {
    flushTimer = null
    flush()
  }, FLUSH_MS)
}

/**
 * 上报一条事件
 * @param {string} event
 * @param {Object} [props]
 */
export function track(event, props) {
  if (!event) return
  const c = currentContext()
  const p = props && typeof props === 'object' ? Object.assign({}, props) : {}
  delete p.platform
  const item = {
    eventId: uuid(),
    event: String(event),
    eventTime: String(nowMs()),
    page: p.page ? String(p.page) : currentPage(),
    sessionId: ensureSession(),
    appVer: '',
    activityCode: p.activity_code || p.activityCode || c.activityCode || '',
    storeCode: p.store_code || p.storeCode || c.storeCode || '',
    source: p.source || c.source || '',
    platform: PLATFORM,
    openid: p.openid || c.openid || '',
    props: p
  }
  delete item.props.activity_code
  delete item.props.activityCode
  delete item.props.store_code
  delete item.props.storeCode
  delete item.props.openid
  const q = loadQueue()
  q.push(item)
  saveQueue(q)
  if (q.length >= FLUSH_SIZE) {
    flush()
  } else {
    scheduleFlush()
  }
}

/**
 * 批量上报；页面卸载时用 sendBeacon，正常时机用 axios
 */
export function flush(useBeacon) {
  if (flushing) return Promise.resolve()
  const q = loadQueue()
  if (!q.length) return Promise.resolve()
  const batch = q.slice(0, MAX_BATCH)
  const rest = q.slice(batch.length)
  saveQueue(rest)
  if (useBeacon && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
    try {
      const url = String(APP_API_BASE || '').replace(/\/$/, '') + '/analytics/collect'
      const blob = new Blob([JSON.stringify({ events: batch })], { type: 'application/json' })
      navigator.sendBeacon(url, blob)
      return Promise.resolve()
    } catch (e) {
      // 落到 axios 兜底
    }
  }
  flushing = true
  return request
    .post('/analytics/collect', { events: batch })
    .then(() => {
      flushing = false
      if (loadQueue().length) scheduleFlush()
    })
    .catch(() => {
      saveQueue(loadQueue().concat(batch))
      flushing = false
      scheduleFlush()
    })
}

/** 页面进入 */
export function trackPartyPageView(page, pageLabel, extra) {
  const p = Object.assign({}, extra || {}, {
    page: page || currentPage(),
    page_label: pageLabel == null ? '' : String(pageLabel)
  })
  track('page_view', p)
}

/** 没有 page_leave 时的兜底：上报停留 */
export function trackPartyPageLeave(startedAt, extra) {
  let duration = typeof startedAt === 'number' ? nowMs() - startedAt : 0
  if (duration < 0) duration = 0
  if (duration > MAX_STAY_MS) duration = MAX_STAY_MS
  track('page_leave', Object.assign({}, extra || {}, { duration_ms: duration }))
}

/** 按钮点击 */
export function trackPartyUiClick(btnId, btnName, extra) {
  if (!btnId) return
  track(
    'ui_click',
    Object.assign({}, extra || {}, {
      btn_id: String(btnId),
      btn_name: btnName == null ? String(btnId) : String(btnName)
    })
  )
}

/** 用户端界面切换（等待/敲击/弹幕/投票/红包/抽奖/入场页） */
export function trackPartyMiniView(view, viewLabel, extra) {
  const v = view == null ? '' : String(view).trim()
  if (!v) return
  track(
    'party_mini_view',
    Object.assign({}, extra || {}, {
      view: v,
      view_label: viewLabel == null ? v : String(viewLabel)
    })
  )
}

/** 入场 */
export function trackPartyEnter(extra) {
  track('party_enter', Object.assign({}, extra || {}))
}

/** 授权/入场成功 */
export function trackPartyJoin(extra) {
  track('party_join', Object.assign({}, extra || {}))
}

/** 互动行为：type=message/like/vote/grab */
export function trackPartyInteract(type, label, extra) {
  track(
    'party_interact',
    Object.assign({}, extra || {}, {
      type: String(type || ''),
      label: label == null ? String(type || '') : String(label)
    })
  )
}

/** 玩法开始/结束 */
export function trackPartyGameStart(extra) {
  track('party_game_start', Object.assign({}, extra || {}))
}

export function trackPartyGameEnd(extra) {
  track('party_game_end', Object.assign({}, extra || {}))
}

/** 分数上报 */
export function trackPartyScore(delta, extra) {
  track(
    'party_score',
    Object.assign({}, extra || {}, {
      delta: Number(delta) || 0
    })
  )
}

/** 心跳：每 30s 上报一次，附本次停留时长 */
export function trackPartyHeartbeat(extra) {
  const stay = heartbeatStartAt ? Math.min(nowMs() - heartbeatStartAt, MAX_STAY_MS) : 0
  track('party_heartbeat', Object.assign({}, extra || {}, { stay_ms: stay }))
}

/** 离开 */
export function trackPartyLeave(extra) {
  const stay = heartbeatStartAt ? Math.min(nowMs() - heartbeatStartAt, MAX_STAY_MS) : 0
  track('party_leave', Object.assign({}, extra || {}, { stay_ms: stay }))
}

function startHeartbeat() {
  if (heartbeatTimer) return
  heartbeatTimer = setInterval(() => {
    if (typeof document !== 'undefined' && document.hidden) return
    trackPartyHeartbeat()
  }, HEARTBEAT_MS)
}

function stopHeartbeat() {
  if (heartbeatTimer) clearInterval(heartbeatTimer)
  heartbeatTimer = null
}

/**
 * 初始化：开始计时/心跳，绑定页面可见性与卸载上报
 */
export function initPartyTrack() {
  if (!heartbeatStartAt) heartbeatStartAt = nowMs()
  startHeartbeat()
  if (visibilityBound || typeof document === 'undefined') return
  visibilityBound = true
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      trackPartyHeartbeat()
      flush(true)
    } else {
      trackPartyPageView(currentPage(), '', { resume: true })
    }
  })
  window.addEventListener('pagehide', () => {
    trackPartyLeave()
    flush(true)
  })
  window.addEventListener('beforeunload', () => {
    flush(true)
  })
}

/** 结束：停心跳，上报离开 */
export function disposePartyTrack() {
  stopHeartbeat()
  trackPartyLeave()
  flush(true)
}

export default {
  track,
  flush,
  initPartyTrack,
  disposePartyTrack,
  setPartyTrackContext,
  getPartyTrackContext,
  clearPartyTrackContext,
  trackPartyPageView,
  trackPartyPageLeave,
  trackPartyUiClick,
  trackPartyMiniView,
  trackPartyEnter,
  trackPartyJoin,
  trackPartyInteract,
  trackPartyGameStart,
  trackPartyGameEnd,
  trackPartyScore,
  trackPartyHeartbeat,
  trackPartyLeave
}
